import { Box, Stack, Text, Badge } from "@mantine/core";
import { Link } from "react-router-dom";
import type { Release } from "@/content/types";
import { hasPublicAudio } from "@/content/releases";
import { MediaImage } from "@/components/common/MediaImage";
import { formatDate } from "@/lib/format";

export function ReleaseCard({ release }: { release: Release }) {
  const isPreRelease = release.status === "pre-release";
  const isAnnounced = release.status === "announced";
  // Dim announced/TBA cards that don't have a public preview clip yet — makes
  // the "we've got something you can hear" vs "just a title for now" split
  // visually obvious. Pre-release entries stay full opacity (they carry their
  // own date + press CTA on the detail page). The dim styling + hover-un-dim
  // are CSS-driven via .dimmed so they cohabit cleanly with .lift-on-hover's
  // transform/box-shadow transitions.
  const dim = isAnnounced && !hasPublicAudio(release);

  // Subtitle: label always shown. Date shown only for pre-release entries
  // (so press / fans see when it lands). Released items drop the date
  // intentionally — the catalog page is a discography, not a timeline.
  // Announced items just show the label (typically "TBA" if undecided).
  let subtitle: string = release.label;
  if (isPreRelease && release.releaseDate) {
    subtitle = `${release.label} · ${formatDate(release.releaseDate)}`;
  }

  return (
    <Box
      component={Link}
      to={`/releases/${release.slug}`}
      style={{
        textDecoration: "none",
        color: "inherit",
        display: "block",
        borderRadius: 12,
        overflow: "hidden",
        border: "1px solid var(--color-ocean)",
      }}
      className={dim ? "lift-on-hover dimmed" : "lift-on-hover"}
    >
      <Box style={{ aspectRatio: "1 / 1", background: "var(--color-deeper)" }}>
        <MediaImage
          image={release.cover}
          sizes="(min-width: 768px) 25vw, 50vw"
          style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
        />
      </Box>
      <Stack gap={2} p="sm">
        <Text fw={600} size="sm" lineClamp={1}>
          {release.title}
        </Text>
        <Text size="xs" c="dimmed" lineClamp={1}>
          {subtitle}
        </Text>
        {isPreRelease && (
          <Badge size="xs" color="magenta" variant="light" mt={4}>
            Pre-release
          </Badge>
        )}
        {isAnnounced && (
          <Badge size="xs" color="cyan" variant="light" mt={4}>
            Upcoming
          </Badge>
        )}
      </Stack>
    </Box>
  );
}
